import React, { useEffect, useState, useRef, useCallback, useMemo } from "react";
import PropTypes from "prop-types";
import throttle from "lodash/throttle";
import get from "lodash/get";
import $ from "jquery";
import "jquery-ui-dist/jquery-ui";
import pubsub from "pubsub-js";
import HamburgerMenu from "@viz-ui/components/hamburgerMenu/hamburgerMenu";
import DataTablePanel from "@viz-ui/components/DataTablePanel/DataTablePanel";
import ChartBaseContainer from "@viz-ui/components/charts/ChartBaseContainer";
import i18n from "@viz-ui/i18n/i18n";
import "./SlidingNavTabs.scss";

// =============================================================================
// CONSTANTS
// =============================================================================

const SCROLL_DIRECTION_LEFT = -1;
const SCROLL_DIRECTION_RIGHT = 1;
const TAB_CONTENT_REDRAW_DELAY = 300;
const SCROLL_THROTTLE_DELAY = 500;
const GLOBAL_NAV_HEIGHT = 64;
const BREADCRUMB_HEIGHT = 65;
const ON_PREMISE_NAV_HEIGHT = 44;
const TAB_CONTAINER_OFFSET = 5;
const PANEL_WIDTH = 350;
const PROCESS_PANEL_WIDTH = 440;
const COPY_INTERPRETATION_PANEL_WIDTH = 440;
const IE_SCROLL_BUFFER = 1;
const DEFAULT_ARROW_WIDTH = 50;

const KEY_CODES = {
  TAB: 9,
  ENTER: 13,
  SPACE: 32,
  LEFT_ARROW: 37,
  RIGHT_ARROW: 39,
};

const CSS_CLASSES = {
  ARROW_TABS_WRAPPER: "arrow-tabs__wrapper",
  TABS_WRAPPER: "tabs__wrapper",
  TABS_WRAPPER_HIDE: "tabs__wrapper--hide",
  TABS: "tabs",
  VISUALIZATION_TAB: "visualizationTab",
  CHART_TABS: "chart-tabs",
  NOT_SORTABLE: "not-sortable",
  ACTIVE: "active",
  DISABLED: "disabled",
  TAB_NAVIGATION_ARROWS: "tab__navigation-arrows",
  TABS_ARROW: "tabs__arrow",
  TABS_ARROW_LEFT: "tabs__arrow--left",
  TABS_ARROW_RIGHT: "tabs__arrow--right",
  TABS_ARROW_HIDE: "tabs__arrow--hide",
  TABS_ARROW_DISABLE: "tabs__arrow--disable",
  TABS_ARROW_FADE_IN: "tabs__arrow--fade-in",
  TABS_ARROW_FADE_OUT: "tabs__arrow--fade-out",
  HAMBURGER_MENU: "tabs__hamburger-menu",
  TABS_CONTENT_CONTAINER: "tabs__content-container",
  TABS_BODY: "tabs__body",
  TABS_ERROR_ICON: "tabs__error-icon",
  DROPDOWN: "dropdown",
  DROPDOWN_OPEN: "open",
  DATATABLE_TAB_DROPDOWN: "datatable-tab-dropdown",
};

const SELECTORS = {
  TABS: ".tabs",
  VISUALIZATION_TAB: ".visualizationTab",
  ADD_CHART_BTN: "#addChartBtn",
  LEFT_ARROW_BTN: ".tab__navigation-arrows button.tabs__arrow.tabs__arrow--left",
  NAV_ARROWS: ".tab__navigation-arrows",
  VIZ_MAIN_PANEL: ".visualizer__main-panel",
  DROPDOWN_OPEN: ".datatable-tab-dropdown.open",
};

// =============================================================================
// UTILS
// =============================================================================

function getHamburgerClasses(isLeftArrowDisable) {
  const fadeClass = isLeftArrowDisable ? CSS_CLASSES.TABS_ARROW_FADE_OUT : CSS_CLASSES.TABS_ARROW_FADE_IN;
  return `${CSS_CLASSES.HAMBURGER_MENU} ${fadeClass}`;
}

function getTabsWrapperClasses(isTabsArrowVisible) {
  return isTabsArrowVisible ? `${CSS_CLASSES.TABS_WRAPPER} ${CSS_CLASSES.TABS_WRAPPER_HIDE}` : CSS_CLASSES.TABS_WRAPPER;
}

function getArrowContainerClasses(isTabsArrowVisible, isRightArrowDisable, isLeftArrowDisable) {
  const parts = [CSS_CLASSES.TAB_NAVIGATION_ARROWS];

  if (isTabsArrowVisible) {
    parts.push(CSS_CLASSES.TABS_ARROW_HIDE);
  }

  // Add left/right class to container based on which arrow is active
  if (!isLeftArrowDisable) {
    parts.push(CSS_CLASSES.TABS_ARROW_LEFT);
  }
  if (!isRightArrowDisable) {
    parts.push(CSS_CLASSES.TABS_ARROW_RIGHT);
  }

  // Add fade state based on right arrow
  parts.push(isRightArrowDisable ? CSS_CLASSES.TABS_ARROW_FADE_OUT : CSS_CLASSES.TABS_ARROW_FADE_IN);

  return parts.join(" ");
}

function getLeftArrowClasses(isLeftArrowDisable) {
  const baseClasses = `${CSS_CLASSES.TABS_ARROW} ${CSS_CLASSES.TABS_ARROW_LEFT} ${CSS_CLASSES.TABS_ARROW_FADE_OUT}`;
  return isLeftArrowDisable ? `${baseClasses} ${CSS_CLASSES.TABS_ARROW_DISABLE}` : baseClasses;
}

function getRightArrowClasses(isRightArrowDisable) {
  const baseClasses = `${CSS_CLASSES.TABS_ARROW} ${CSS_CLASSES.TABS_ARROW_RIGHT} ${CSS_CLASSES.TABS_ARROW_FADE_OUT}`;
  return isRightArrowDisable ? `${baseClasses} ${CSS_CLASSES.TABS_ARROW_DISABLE}` : baseClasses;
}

function getTabClasses(isActive, isDisabled) {
  const parts = [CSS_CLASSES.VISUALIZATION_TAB];
  if (isActive) parts.push(CSS_CLASSES.ACTIVE);
  if (isDisabled) parts.push(CSS_CLASSES.DISABLED);
  return parts.join(" ");
}

function getHeadingClasses(isSortable) {
  return isSortable ? CSS_CLASSES.CHART_TABS : `${CSS_CLASSES.CHART_TABS} ${CSS_CLASSES.NOT_SORTABLE}`;
}

function getDropdownClasses(isOpen) {
  const baseClasses = `${CSS_CLASSES.DROPDOWN} ${CSS_CLASSES.DATATABLE_TAB_DROPDOWN}`;
  return isOpen ? `${baseClasses} ${CSS_CLASSES.DROPDOWN_OPEN}` : baseClasses;
}

function getArrowWidth() {
  const arrowsElement = document.querySelector(SELECTORS.NAV_ARROWS);
  return arrowsElement ? arrowsElement.offsetWidth + 2 : DEFAULT_ARROW_WIDTH;
}

function calculateArrowScrollPosition(container, direction) {
  if (!container) return 0;
  const containerWidth = container.offsetWidth;
  const maxScrollPosition = container.scrollWidth - containerWidth;
  const currentScrollPosition = container.scrollLeft;
  const arrowWidth = getArrowWidth();

  let scrolling = currentScrollPosition;
  scrolling += containerWidth * direction;
  scrolling -= direction * (arrowWidth * 2);

  if (direction > 0) {
    scrolling = Math.min(scrolling, maxScrollPosition);
  } else if (direction < 0) {
    scrolling = Math.max(scrolling, 0);
  }
  return scrolling;
}

function smoothScrollTo(container, position) {
  if (container) {
    container.scrollTo({ left: position, behavior: "smooth" });
  }
}

function getTabIds(tabs) {
  if (!tabs || !Array.isArray(tabs)) return [];
  return tabs.map(tab => (tab ? tab.id : null));
}

function isTabSortableIndex(tabIndex) {
  return tabIndex > 0;
}

// =============================================================================
// SUB-COMPONENTS
// =============================================================================

function NavigationArrows({
  isTabsArrowVisible,
  isLeftArrowDisable,
  isRightArrowDisable,
  onLeftArrowClick,
  onRightArrowClick,
}) {
  const containerClasses = getArrowContainerClasses(isTabsArrowVisible, isRightArrowDisable, isLeftArrowDisable);
  const leftClasses = getLeftArrowClasses(isLeftArrowDisable);
  const rightClasses = getRightArrowClasses(isRightArrowDisable);

  return (
    <div className={containerClasses}>
      <button
        type="button"
        aria-label={i18n.t("_ChartTab.Previous.Label_")}
        className={leftClasses}
        onClick={onLeftArrowClick}
      >
        <i className="acl-i-arrow-left" />
      </button>
      <button
        type="button"
        aria-label={i18n.t("_ChartTab.Next.Label_")}
        className={rightClasses}
        onClick={onRightArrowClick}
      >
        <i className="acl-i-arrow-right" />
      </button>
    </div>
  );
}

NavigationArrows.propTypes = {
  isTabsArrowVisible: PropTypes.bool,
  isLeftArrowDisable: PropTypes.bool,
  isRightArrowDisable: PropTypes.bool,
  onLeftArrowClick: PropTypes.func.isRequired,
  onRightArrowClick: PropTypes.func.isRequired,
};

NavigationArrows.defaultProps = {
  isTabsArrowVisible: false,
  isLeftArrowDisable: false,
  isRightArrowDisable: false,
};

function Tab({
  tab,
  index,
  isActive,
  isDisabled,
  isSortable,
  hasError,
  isMenuOpen,
  onTabClick,
  onTabKeyDown,
  onToggleMenu,
  onMenuItemClick,
}) {
  const { data = {}, title } = tab;
  const tabIcon = data.tabIcon || "data-table";
  const tabTitle = title || i18n.t("_Chart.Title.Default_");
  const { menu } = data;

  const tabClasses = getTabClasses(isActive, isDisabled);
  const headingClasses = getHeadingClasses(isSortable);

  return (
    <dd
      className={tabClasses}
      role="tab"
      aria-label={tabTitle}
      aria-selected={isActive}
      tabIndex={isActive ? 0 : -1}
      data-tab-id={tab.id}
      onClick={e => onTabClick(e, index)}
      onKeyDown={e => onTabKeyDown(e, index)}
    >
      <a
        href="#"
        onClick={e => e.preventDefault()}
        id={`tab-${index + 1}`}
        role="button"
        tabIndex={-1}
        aria-label={tabTitle}
      >
        <span className={headingClasses}>
          <i className={`datatable-tab-icon icon-${tabIcon}`} />
          <span className="chart-title" title={tabTitle}>
            {tabTitle}
          </span>

          {hasError && (
            <span className="tabs__error-icon">
              <i className="acl-i-exclamation-circle" style={{ color: "#bf371f" }} />
            </span>
          )}

          {menu && menu.length > 0 && (
            <div style={{ display: "inline-block" }}>
              <div className={getDropdownClasses(isMenuOpen)}>
                <i
                  id={`showTabMenuBtn-${index}`}
                  className="icon-chevron-down"
                  data-toggle="dropdown"
                  role="button"
                  tabIndex={0}
                  onClick={e => onToggleMenu(e, index)}
                  onKeyDown={e => {
                    if (e.key === "Enter" || e.key === " ") {
                      onToggleMenu(e, index);
                    }
                  }}
                />
                <div className="dropdown_wrapper">
                  <div className="dropdown_arrow" />
                  <ul className="dropdown-menu" role="menu">
                    {menu.map((menuItem, menuItemIndex) => (
                      <li key={`menu-${index}-${menuItemIndex}`}>
                        <a
                          href="#"
                          id={`tabMenu-${index}-item-${menuItemIndex}`}
                          onClick={e => {
                            e.preventDefault();
                            onMenuItemClick(e, menuItem);
                          }}
                          className="dropdown-menu-button"
                        >
                          <i className={`icon-${menuItem.icon}`} />
                          <span>{menuItem.text}</span>
                        </a>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          )}
        </span>
      </a>
    </dd>
  );
}

Tab.propTypes = {
  tab: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    title: PropTypes.string,
    data: PropTypes.shape({
      tabIcon: PropTypes.string,
      menu: PropTypes.arrayOf(
        PropTypes.shape({
          icon: PropTypes.string,
          text: PropTypes.string,
          click: PropTypes.func,
        })
      ),
    }),
  }).isRequired,
  index: PropTypes.number.isRequired,
  isActive: PropTypes.bool,
  isDisabled: PropTypes.bool,
  isSortable: PropTypes.bool,
  hasError: PropTypes.bool,
  isMenuOpen: PropTypes.bool,
  onTabClick: PropTypes.func.isRequired,
  onTabKeyDown: PropTypes.func.isRequired,
  onToggleMenu: PropTypes.func.isRequired,
  onMenuItemClick: PropTypes.func.isRequired,
};

Tab.defaultProps = {
  isActive: false,
  isDisabled: false,
  isSortable: false,
  hasError: false,
  isMenuOpen: false,
};

// =============================================================================
// MAIN COMPONENT
// =============================================================================

function SlidingNavTabs(props) {
  const {
    disableAllTabs,
    tabs,
    selectedTabIndex,
    tabProps,
    tabChanged,
    isTabUpdated,
    isFilterPanelOpen,
    isConfigPanelOpen,
    isProcessPanelOpen,
    isCopyInterpretationOpen,
    fieldFormatIdentifier,
    appConfig,
    dataModel,
    eventService,
    dataFilter,
    localize,
    tabStateService,
    columnConfig,
    field,
    processPanelService,
    chartService,
  } = props;

  const safeTabs = tabs || [];

  // ===========================================================================
  // TAB STATE & SELECTION (Must be declared early as used by other hooks)
  // ===========================================================================
  const [activeTabIndex, setActiveTabIndex] = useState(selectedTabIndex || 0);
  const activeTabIdRef = useRef(null);
  const isDraggingRef = useRef(false);
  const [dragActiveTabId, setDragActiveTabId] = useState(null);
  const [mountedTabIds, setMountedTabIds] = useState(new Set());
  const postDragRef = useRef(false);

  // ===========================================================================
  // CONTENT HEIGHT
  // ===========================================================================
  const wrapperRef = useRef(null);
  const [tabContentHeight, setTabContentHeight] = useState({ height: "100%" });

  const adjustWindowSize = useCallback(() => {
    let height;
    let contentHeightOffset = 0;
    const windowInnerHeight = window.innerHeight;
    let tabsElement = null;

    if (wrapperRef.current) {
      tabsElement = wrapperRef.current.closest(".tabs");
    }
    if (!tabsElement) {
      tabsElement = document.querySelector("dl.tabs");
    }

    if (tabsElement && wrapperRef.current) {
      const showBreadcrumbFromViz = get(appConfig, "features.highbondNavigationInVisualizer", false);
      const showBreadcrumbFromResults = get(appConfig, "features.featureVizBreadcrumb", false);
      const tabsOffset =
        showBreadcrumbFromViz || showBreadcrumbFromResults
          ? GLOBAL_NAV_HEIGHT + BREADCRUMB_HEIGHT
          : ON_PREMISE_NAV_HEIGHT;

      // offsetHeight already includes padding and borders, no need to add them again
      const tabsOuterHeight = tabsElement.offsetHeight;

      const topPosition = tabsOuterHeight - TAB_CONTAINER_OFFSET;

      contentHeightOffset = tabsOffset + tabsOuterHeight - TAB_CONTAINER_OFFSET;
      height = windowInnerHeight - contentHeightOffset;

      setTabContentHeight({
        top: topPosition + "px",
        height: height + "px",
      });
      return;
    }

    setTabContentHeight({ height: "100%" });
  }, [appConfig]);

  useEffect(() => {
    const timeoutId = setTimeout(adjustWindowSize, 0);
    let unsubscribe;
    if (eventService && typeof eventService.register === "function") {
      const registeredService = eventService.register("SlidingNavTabs.Height");
      if (registeredService && typeof registeredService.subscribe === "function") {
        unsubscribe = registeredService.subscribe("windowResizeEvent", adjustWindowSize);
      }
    }
    window.addEventListener("resize", adjustWindowSize);
    return () => {
      clearTimeout(timeoutId);
      if (unsubscribe) unsubscribe();
      window.removeEventListener("resize", adjustWindowSize);
    };
  }, [adjustWindowSize, eventService]);

  // ===========================================================================
  // CONTENT WIDTH
  // ===========================================================================
  const [contentWidthStyle, setContentWidthStyle] = useState({ width: "auto" });
  const resizeTimerRef = useRef(null);
  const currentTotalPanelsWidthRef = useRef(0);

  const getTotalPanelsWidth = useCallback(() => {
    let panelsWidth = 0;
    if (isProcessPanelOpen) {
      panelsWidth += PROCESS_PANEL_WIDTH;
    } else if (isCopyInterpretationOpen) {
      panelsWidth += COPY_INTERPRETATION_PANEL_WIDTH;
    } else {
      if (isConfigPanelOpen) panelsWidth += PANEL_WIDTH;
      if (isFilterPanelOpen) panelsWidth += PANEL_WIDTH;
    }
    return panelsWidth;
  }, [isFilterPanelOpen, isConfigPanelOpen, isProcessPanelOpen, isCopyInterpretationOpen]);

  const updateContentWidth = useCallback(
    force => {
      if (resizeTimerRef.current) clearTimeout(resizeTimerRef.current);
      const totalPanelsWidth = getTotalPanelsWidth();

      if (totalPanelsWidth !== currentTotalPanelsWidthRef.current || force) {
        resizeTimerRef.current = setTimeout(() => {
          currentTotalPanelsWidthRef.current = totalPanelsWidth;

          // Match Angular exactly: get vizMainPanel width and subtract panels
          const vizMainPanel = document.querySelector(SELECTORS.VIZ_MAIN_PANEL);
          const vizMainPanelWidth = vizMainPanel ? vizMainPanel.offsetWidth : window.innerWidth;

          // Angular logic: width = totalPanelsWidth > 0 ? vizMainPanelWidth - totalPanelsWidth : "auto"
          // Now that content container is a sibling (not nested), this works correctly
          setContentWidthStyle({
            width: totalPanelsWidth > 0 ? vizMainPanelWidth - totalPanelsWidth : "auto",
          });

          resizeTimerRef.current = null;

          if (dataModel && dataModel.interpretation && typeof dataModel.interpretation.currentTabIndex === "function") {
            const currentTabIndex = dataModel.interpretation.currentTabIndex();
            if (currentTabIndex <= 0) {
              pubsub.publish("tableRedraw");
            } else {
              pubsub.publish("chartRedraw");
            }
          }
        }, TAB_CONTENT_REDRAW_DELAY);
      }
    },
    [getTotalPanelsWidth, dataModel]
  );

  useEffect(() => {
    requestAnimationFrame(() => {
      updateContentWidth(true);
    });
    let unsubWindowResize = null;
    let unsubPanelsChange = null;

    if (eventService && typeof eventService.register === "function") {
      const eventServiceRef = eventService.register("SlidingNavTabs.Width", null);
      if (eventServiceRef && typeof eventServiceRef.subscribe === "function") {
        unsubWindowResize = eventServiceRef.subscribe("windowResizeEvent", () => updateContentWidth(true));
        unsubPanelsChange = eventServiceRef.subscribe("panelsStateChange", () => updateContentWidth(false));
      }
    }
    const handleResize = () => updateContentWidth(true);
    window.addEventListener("resize", handleResize);

    return () => {
      if (resizeTimerRef.current) clearTimeout(resizeTimerRef.current);
      if (unsubWindowResize) unsubWindowResize();
      if (unsubPanelsChange) unsubPanelsChange();
      window.removeEventListener("resize", handleResize);
    };
  }, [updateContentWidth, eventService]);

  useEffect(() => {
    updateContentWidth(false);
  }, [isFilterPanelOpen, isConfigPanelOpen, isProcessPanelOpen, isCopyInterpretationOpen, updateContentWidth]);

  useEffect(() => {
    if (dataModel && dataModel.interpretation) {
      const timeoutId = setTimeout(() => {
        if (activeTabIndex <= 0) {
          pubsub.publish("tableRedraw");
        } else {
          pubsub.publish("chartRedraw");
        }
      }, 100);
      return () => clearTimeout(timeoutId);
    }
  }, [activeTabIndex, dataModel]);

  const chartServiceRef = useRef(chartService);

  useEffect(() => {
    chartServiceRef.current = chartService;
  }, [chartService]);

  useEffect(() => {
    let unsubscribe;
    if (eventService && typeof eventService.register === "function") {
      const registeredService = eventService.register("SlidingNavTabs.ColorSync");
      if (registeredService && typeof registeredService.subscribe === "function") {
        unsubscribe = registeredService.subscribe(
          "react.chart.setGlobalChartElementsColorsTableByKey",
          (msg, key, color) => {
            if (
              chartServiceRef.current &&
              typeof chartServiceRef.current.setGlobalChartElementsColorsTableByKey === "function"
            ) {
              chartServiceRef.current.setGlobalChartElementsColorsTableByKey(key, color);
            }
          }
        );
      }
    }
    return () => {
      if (unsubscribe) unsubscribe();
    };
  }, [eventService]);

  // ===========================================================================
  // TAB SCROLLING
  // ===========================================================================
  const tabsContainerRef = useRef(null);
  const [isTabsArrowVisible, setIsTabsArrowVisible] = useState(false);
  const [isLeftArrowDisable, setIsLeftArrowDisable] = useState(false);
  const [isRightArrowDisable, setIsRightArrowDisable] = useState(false);

  const calculateArrowStates = container => {
    if (!container) return { isTabsArrowVisible: true, isLeftArrowDisable: true, isRightArrowDisable: true };
    const scroll = container.scrollLeft;
    const maxScroll = container.scrollWidth - container.offsetWidth;
    const result = { isTabsArrowVisible: true, isLeftArrowDisable: true, isRightArrowDisable: true };

    const hasOverflow = maxScroll > IE_SCROLL_BUFFER + 1;

    if (!hasOverflow) {
      result.isTabsArrowVisible = true;
      result.isLeftArrowDisable = true;
      result.isRightArrowDisable = true;
      return result;
    }

    result.isTabsArrowVisible = false;

    if (scroll <= IE_SCROLL_BUFFER) {
      result.isLeftArrowDisable = true;
    } else {
      result.isLeftArrowDisable = false;
    }

    const isAtEnd = scroll >= Math.max(0, maxScroll - IE_SCROLL_BUFFER - 2);
    if (isAtEnd) {
      result.isRightArrowDisable = true;
    } else {
      result.isRightArrowDisable = false;
    }

    return result;
  };

  const updateTabsClasses = useCallback(() => {
    const container = tabsContainerRef.current;
    if (!container) return;

    requestAnimationFrame(() => {
      const states = calculateArrowStates(container);
      setIsTabsArrowVisible(states.isTabsArrowVisible);
      setIsLeftArrowDisable(states.isLeftArrowDisable);
      setIsRightArrowDisable(states.isRightArrowDisable);
    });
  }, []);

  const throttledUpdateTabsClasses = useCallback(throttle(updateTabsClasses, SCROLL_THROTTLE_DELAY), [
    updateTabsClasses,
  ]);

  const immediateUpdateTabsClasses = useCallback(() => {
    updateTabsClasses();
  }, [updateTabsClasses]);

  const scrollToTab = useCallback(tabIndex => {
    const container = tabsContainerRef.current;
    if (!container || tabIndex === undefined) return;
    const tab = container.querySelector(`.visualizationTab:nth-child(${tabIndex + 1})`);
    if (!tab) return;

    const arrowWidth = getArrowWidth();
    const tabPosition = tab.offsetLeft - container.scrollLeft;
    const tabWidth = tab.offsetWidth;
    const containerWidth = container.offsetWidth;
    const currentScrollPosition = container.scrollLeft;
    const maxScrollPosition = container.scrollWidth;

    let scrollTo = currentScrollPosition;

    // Check if tab needs to scroll right (hidden on the right side)
    if (containerWidth - arrowWidth < tabPosition + tabWidth) {
      scrollTo += tabPosition - containerWidth + tabWidth + arrowWidth;
      scrollTo = Math.min(scrollTo, maxScrollPosition);
      smoothScrollTo(container, scrollTo);
    } else if (tabPosition < arrowWidth) {
      // Check if tab needs to scroll left (hidden on the left side)
      scrollTo += tabPosition - arrowWidth;
      smoothScrollTo(container, Math.max(scrollTo, 0));
    }
  }, []);

  useEffect(() => {
    const container = tabsContainerRef.current;
    if (!container) return;

    container.addEventListener("scroll", throttledUpdateTabsClasses);

    const handleWheel = e => {
      if (container) container.scrollLeft -= e.deltaY;
    };
    container.addEventListener("wheel", handleWheel);

    // Calculate arrow states after tabs are rendered
    requestAnimationFrame(() => {
      updateTabsClasses();
    });

    window.addEventListener("resize", throttledUpdateTabsClasses);

    return () => {
      container.removeEventListener("scroll", throttledUpdateTabsClasses);
      container.removeEventListener("wheel", handleWheel);
      window.removeEventListener("resize", throttledUpdateTabsClasses);
    };
  }, [throttledUpdateTabsClasses, updateTabsClasses]);

  useEffect(() => {
    if (selectedTabIndex !== undefined) {
      // Small delay to ensure DOM is updated and arrows are rendered
      setTimeout(() => {
        scrollToTab(selectedTabIndex);
      }, 50);
    }
  }, [selectedTabIndex, scrollToTab]);

  const handleLeftArrowClick = useCallback(() => {
    const container = tabsContainerRef.current;
    if (container) {
      smoothScrollTo(container, calculateArrowScrollPosition(container, SCROLL_DIRECTION_LEFT));
      setTimeout(() => {
        immediateUpdateTabsClasses();
      }, 600);
    }
  }, [immediateUpdateTabsClasses]);

  const handleRightArrowClick = useCallback(() => {
    const container = tabsContainerRef.current;
    if (container) {
      smoothScrollTo(container, calculateArrowScrollPosition(container, SCROLL_DIRECTION_RIGHT));
      setTimeout(() => {
        immediateUpdateTabsClasses();
      }, 600);
    }
  }, [immediateUpdateTabsClasses]);

  // ===========================================================================
  // TAB REORDERING
  // ===========================================================================
  const oldTabIdsRef = useRef(getTabIds(safeTabs));

  useEffect(() => {
    // Skip updates during drag to prevent flickering
    // Using global flag because parent updates tabs AFTER drag completes,
    // and we need to block this useEffect until everything is ready
    if (window.__tabSortingInProgress) {
      return;
    }

    oldTabIdsRef.current = getTabIds(safeTabs);

    // Recalculate arrow states when tabs change
    requestAnimationFrame(() => {
      updateTabsClasses();
    });
  }, [safeTabs, updateTabsClasses]);

  const isTabSortable = index => isTabSortableIndex(index);

  // jQuery UI Sortable - drag and drop functionality for tabs
  useEffect(() => {
    const container = tabsContainerRef.current;
    if (!container || typeof $ === "undefined" || !$.fn || !$.fn.sortable) {
      console.warn("SlidingNavTabs: jQuery UI Sortable not available");
      return;
    }

    const $container = $(container);
    let tabsOrderChanged = false;

    $container.sortable({
      axis: "x",
      items: "dd.visualizationTab:not(:first)", // First tab (Table View) is NOT draggable
      tolerance: "pointer",
      forcePlaceholderSize: true,
      start: function() {
        isDraggingRef.current = true;
        window.__tabSortingInProgress = true;
        setDragActiveTabId(activeTabIdRef.current);
      },
      update: function() {
        tabsOrderChanged = true;
      },
      stop: function() {
        if (tabsOrderChanged) {
          requestAnimationFrame(() => {
            const newTabIds = Array.from(container.querySelectorAll("dd.visualizationTab")).map(dd =>
              dd.getAttribute("data-tab-id")
            );

            const oldTabIds = oldTabIdsRef.current;
            const oldVisualizationIds = oldTabIds.slice(1);
            const newVisualizationIds = newTabIds.slice(1);
            const mapping = oldVisualizationIds.map(id => newVisualizationIds.indexOf(id));

            if (dataModel && typeof dataModel.reorderVisualizations === "function") {
              dataModel.reorderVisualizations(mapping);
            }

            const currentIndex =
              dataModel && dataModel.interpretation && typeof dataModel.interpretation.currentTabIndex === "function"
                ? dataModel.interpretation.currentTabIndex()
                : -1;

            if (eventService && typeof eventService.publish === "function") {
              eventService.publish("tabs.reordered", currentIndex);
            }

            // Defer activeTabIndex update to postDrag useEffect once safeTabs syncs
            tabsOrderChanged = false;
            window.__tabSortingInProgress = false;
            postDragRef.current = true;

            setTimeout(() => {
              if (postDragRef.current) {
                postDragRef.current = false;
                isDraggingRef.current = false;
                setDragActiveTabId(null);
              }
            }, 1000);
          });
        } else {
          window.__tabSortingInProgress = false;
          isDraggingRef.current = false;
          setDragActiveTabId(null);
        }
      },
    });

    return () => {
      if ($container.sortable("instance")) {
        $container.sortable("destroy");
      }
      window.__tabSortingInProgress = false;
    };
  }, [dataModel, eventService]);

  useEffect(() => {
    if (safeTabs[activeTabIndex]) {
      activeTabIdRef.current = safeTabs[activeTabIndex].id;
    }
  }, [activeTabIndex, safeTabs]);

  // Resolve activeTabIndex after drag-and-drop once safeTabs syncs with DataModel
  useEffect(() => {
    if (!postDragRef.current) return;

    if (dragActiveTabId) {
      const newIndex = safeTabs.findIndex(tab => tab && tab.id === dragActiveTabId);
      if (newIndex !== -1) {
        setActiveTabIndex(newIndex);
        if (tabChanged) {
          tabChanged({ tabIndex: newIndex });
        }
      }
    }

    postDragRef.current = false;
    isDraggingRef.current = false;
    setDragActiveTabId(null);
  }, [safeTabs, dragActiveTabId, tabChanged]);

  // Sync activeTabIndex with Angular's selectedTabIndex prop
  useEffect(() => {
    if (isDraggingRef.current || postDragRef.current) return;
    setActiveTabIndex(selectedTabIndex || 0);
  }, [selectedTabIndex]);

  // Track visited chart tabs for lazy persistent mounting
  useEffect(() => {
    if (activeTabIndex > 0) {
      const tab = safeTabs[activeTabIndex];
      if (tab && tab.id) {
        setMountedTabIds(prev => {
          if (prev.has(tab.id)) return prev;
          const next = new Set(prev);
          next.add(tab.id);
          return next;
        });
      }
    }
  }, [activeTabIndex, safeTabs]);

  const handleTabClick = useCallback(
    (e, tabIndex) => {
      const target = e.currentTarget;
      const isDisabled = target && target.classList && target.classList.contains("disabled");
      if (isDisabled || postDragRef.current) return;
      if (tabIndex !== activeTabIndex) {
        setActiveTabIndex(tabIndex);
        // Defer Angular notification so React processes setActiveTabIndex first,
        // preventing reactDirective from forcing a re-render with stale activeTabIndex
        Promise.resolve().then(() => {
          if (tabChanged) {
            tabChanged({ tabIndex });
          }
        });
      }
    },
    [tabChanged, activeTabIndex]
  );

  // ===========================================================================
  // TAB ERROR STATE
  // ===========================================================================
  const [tabHasError, setTabHasError] = useState({});
  const updateTabErrorState = useCallback(() => {
    if (!dataModel || !chartService || typeof dataModel.visualizations !== "function") return;
    const visualizations = dataModel.visualizations();
    const newTabHasError = {};
    if (visualizations && Array.isArray(visualizations)) {
      visualizations.forEach((viz, vizIndex) => {
        newTabHasError[vizIndex] = chartService.chartHasError(vizIndex);
      });
    }
    setTabHasError(newTabHasError);
  }, [dataModel, chartService]);

  useEffect(() => {
    if (!dataModel || typeof dataModel.subscribe !== "function") return;
    updateTabErrorState();
    const unsubViz = dataModel.subscribe("visualizations", updateTabErrorState);
    const unsubFields = dataModel.subscribe("table.fields", updateTabErrorState);
    return () => {
      if (unsubViz) unsubViz();
      if (unsubFields) unsubFields();
    };
  }, [dataModel, updateTabErrorState]);

  // ===========================================================================
  // KEYBOARD NAVIGATION
  // ===========================================================================
  const handleTabKeyDown = useCallback(
    (e, tabIndex) => {
      if (e.shiftKey) return;
      const target = e.currentTarget;
      switch (e.keyCode) {
        case KEY_CODES.TAB:
          e.preventDefault();
          if (isTabsArrowVisible) {
            const addChartBtn = document.querySelector(SELECTORS.ADD_CHART_BTN);
            if (addChartBtn) addChartBtn.focus();
          } else {
            const leftArrowBtn = document.querySelector(SELECTORS.LEFT_ARROW_BTN);
            if (leftArrowBtn) leftArrowBtn.focus();
          }
          break;
        case KEY_CODES.ENTER:
        case KEY_CODES.SPACE:
          e.preventDefault();
          handleTabClick(e, tabIndex);
          break;
        case KEY_CODES.LEFT_ARROW:
          if (target.previousElementSibling) target.previousElementSibling.focus();
          break;
        case KEY_CODES.RIGHT_ARROW:
          if (target.nextElementSibling) target.nextElementSibling.focus();
          break;
        default:
          break;
      }
    },
    [isTabsArrowVisible, handleTabClick]
  );

  // ===========================================================================
  // DROPDOWN MENU
  // ===========================================================================
  const [openMenuIndex, setOpenMenuIndex] = useState(-1);

  const toggleMenu = useCallback((e, index) => {
    e.preventDefault();
    e.stopPropagation();
    setOpenMenuIndex(prev => (prev === index ? -1 : index));
  }, []);

  const handleMenuItemClick = useCallback((e, menuItem) => {
    e.preventDefault();
    e.stopPropagation();
    if (menuItem && typeof menuItem.click === "function") menuItem.click();
    setOpenMenuIndex(-1);
  }, []);

  const isMenuOpen = useCallback(index => openMenuIndex === index, [openMenuIndex]);

  useEffect(() => {
    const handleDocumentClick = event => {
      if (openMenuIndex !== -1) {
        const dropdown = document.querySelector(SELECTORS.DROPDOWN_OPEN);
        if (dropdown && !dropdown.contains(event.target)) {
          setOpenMenuIndex(-1);
        }
      }
    };
    document.addEventListener("click", handleDocumentClick);
    return () => document.removeEventListener("click", handleDocumentClick);
  }, [openMenuIndex]);

  // ===========================================================================
  // RENDER CONTENT LOGIC
  // ===========================================================================
  const activeTab = safeTabs[activeTabIndex];
  const currentTabProps = (tabProps || {})[activeTabIndex] || {};

  const callbackInterface = useMemo(() => (activeTab ? activeTab.callbackInterface || {} : {}), [activeTab]);

  const currentTabId = (activeTab && activeTab.id) || `tab-${activeTabIndex}`;

  const wrappedOnReload = useMemo(() => (callbackInterface.onReload ? callbackInterface.onReload : undefined), [
    callbackInterface.onReload,
  ]);

  const wrappedOnScrollToBottom = useMemo(
    () => (callbackInterface.onScrollToBottom ? callbackInterface.onScrollToBottom : undefined),
    [callbackInterface.onScrollToBottom]
  );

  const wrappedDisableAllButtons = useMemo(
    () => (callbackInterface.disableAllButtons ? callbackInterface.disableAllButtons : undefined),
    [callbackInterface.disableAllButtons]
  );

  const wrappedEnableAllButtons = useMemo(
    () => (callbackInterface.enableAllButtons ? callbackInterface.enableAllButtons : undefined),
    [callbackInterface.enableAllButtons]
  );

  const wrappedOnDeleteRows = useMemo(
    () =>
      callbackInterface.onDeleteRows
        ? ({ recordIds, mode }) => callbackInterface.onDeleteRows(recordIds, mode)
        : undefined,
    [callbackInterface.onDeleteRows]
  );

  const wrappedOnAddComment = useMemo(
    () =>
      callbackInterface.onAddComment ? ({ commentCounts }) => callbackInterface.onAddComment(commentCounts) : undefined,
    [callbackInterface.onAddComment]
  );

  const wrappedOnDeleteInProgress = useMemo(
    () =>
      callbackInterface.onDeleteInProgress ? ({ message }) => callbackInterface.onDeleteInProgress(message) : undefined,
    [callbackInterface.onDeleteInProgress]
  );

  const wrappedOnBulkUpdate = useMemo(
    () =>
      callbackInterface.onBulkUpdate
        ? ({ tableRefresh, message, messageType, hardTableRefresh, isDeletion }) =>
            callbackInterface.onBulkUpdate(tableRefresh, message, messageType, hardTableRefresh, isDeletion)
        : undefined,
    [callbackInterface.onBulkUpdate]
  );

  const wrappedOnProcessRecord = useMemo(
    () =>
      callbackInterface.onProcessRecord
        ? ({ processed, rowIdKey, newFieldData, removeFromView }) =>
            callbackInterface.onProcessRecord(processed, rowIdKey, newFieldData, removeFromView)
        : undefined,
    [callbackInterface.onProcessRecord]
  );

  const renderTableContent = useCallback(() => {
    if (activeTabIndex !== 0 || !activeTab) return null;

    return (
      <DataTablePanel
        key={`table-${currentTabId}`}
        commentCounts={currentTabProps.commentCounts}
        data={currentTabProps.data}
        fields={currentTabProps.fields}
        filteredRecordCount={currentTabProps.filteredRecordCount}
        fieldFormatIdentifier={currentTabProps.fieldFormatIdentifier || fieldFormatIdentifier}
        tabIndex={activeTabIndex}
        onReload={wrappedOnReload}
        onScrollToBottom={wrappedOnScrollToBottom}
        onDeleteRows={wrappedOnDeleteRows}
        onAddComment={wrappedOnAddComment}
        disableAllButtons={wrappedDisableAllButtons}
        enableAllButtons={wrappedEnableAllButtons}
        onDeleteInProgress={wrappedOnDeleteInProgress}
        onBulkUpdate={wrappedOnBulkUpdate}
        onProcessRecord={wrappedOnProcessRecord}
        isConfigPanelOpen={isConfigPanelOpen}
        appConfig={appConfig}
        dataModel={dataModel}
        eventService={eventService}
        dataFilter={dataFilter}
        localize={localize}
        tabStateService={tabStateService}
        columnConfigClass={columnConfig}
        fieldClass={field}
        processPanelService={processPanelService}
      />
    );
  }, [
    activeTab,
    activeTabIndex,
    currentTabId,
    currentTabProps,
    fieldFormatIdentifier,
    wrappedOnReload,
    wrappedOnScrollToBottom,
    wrappedDisableAllButtons,
    wrappedEnableAllButtons,
    wrappedOnDeleteRows,
    wrappedOnAddComment,
    wrappedOnDeleteInProgress,
    wrappedOnBulkUpdate,
    wrappedOnProcessRecord,
    isConfigPanelOpen,
    appConfig,
    dataModel,
    eventService,
    dataFilter,
    localize,
    tabStateService,
    columnConfig,
    field,
    processPanelService,
  ]);

  const handleHamburgerClick = useCallback(
    data => {
      if (data && data.event !== undefined) {
        handleTabClick(data.event, data.tabIndex);
      }
    },
    [handleTabClick]
  );

  return (
    <>
      {/* Tabs Header Section - matches Angular's arrow-tabs__wrapper */}
      <div ref={wrapperRef} className={CSS_CLASSES.ARROW_TABS_WRAPPER}>
        {/* Hamburger Menu */}
        {isTabUpdated && (
          <div className={getHamburgerClasses(isLeftArrowDisable)}>
            <HamburgerMenu tabList={safeTabs} onClick={handleHamburgerClick} />
          </div>
        )}

        {/* Tabs Wrapper */}
        <div ref={tabsContainerRef} className={getTabsWrapperClasses(isTabsArrowVisible)}>
          {safeTabs.map((tab, index) => {
            // During drag, use ID-based matching to prevent flickering
            // After drag, use index-based matching for normal behavior
            const isActive = dragActiveTabId !== null ? tab.id === dragActiveTabId : index === activeTabIndex;
            return (
              <Tab
                key={tab.id || index}
                tab={tab}
                index={index}
                isActive={isActive}
                isDisabled={disableAllTabs}
                isSortable={isTabSortable(index)}
                hasError={tabHasError[index - 1]}
                isMenuOpen={isMenuOpen(index)}
                onTabClick={handleTabClick}
                onTabKeyDown={handleTabKeyDown}
                onToggleMenu={toggleMenu}
                onMenuItemClick={handleMenuItemClick}
              />
            );
          })}
        </div>

        {/* Navigation Arrows */}
        <NavigationArrows
          isTabsArrowVisible={isTabsArrowVisible}
          isLeftArrowDisable={isLeftArrowDisable}
          isRightArrowDisable={isRightArrowDisable}
          onLeftArrowClick={handleLeftArrowClick}
          onRightArrowClick={handleRightArrowClick}
        />
      </div>

      {/* Content Container - SIBLING to arrow-tabs__wrapper (matches Angular's .tabs-content) */}
      <div className={CSS_CLASSES.TABS_CONTENT_CONTAINER} style={tabContentHeight}>
        <div className={CSS_CLASSES.TABS_BODY} style={contentWidthStyle}>
          {renderTableContent()}

          {/* Lazy persistent rendering: mount on first visit, hide with display:none */}
          {safeTabs.map((tab, index) => {
            if (index === 0) return null;
            const isActive = dragActiveTabId !== null ? tab.id === dragActiveTabId : index === activeTabIndex;
            const shouldMount = isActive || mountedTabIds.has(tab.id);
            if (!shouldMount) return null;
            const chartData = (tab && tab.data) || {};
            return (
              <div key={tab.id || `chart-${index}`} style={{ display: isActive ? undefined : "none", height: "100%" }}>
                <ChartBaseContainer
                  chartType={chartData.vizType}
                  chartData={chartData}
                  index={index}
                  interpretationId={fieldFormatIdentifier}
                  dataModel={dataModel}
                  appConfig={appConfig}
                  eventService={eventService}
                  tabStateService={tabStateService}
                />
              </div>
            );
          })}
        </div>
      </div>
    </>
  );
}

SlidingNavTabs.propTypes = {
  disableAllTabs: PropTypes.bool,
  tabs: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      title: PropTypes.string,
      active: PropTypes.bool,
      data: PropTypes.object,
      tabIndex: PropTypes.number,
    })
  ),
  selectedTabIndex: PropTypes.number,
  tabProps: PropTypes.object,
  tabChanged: PropTypes.func,
  isTabUpdated: PropTypes.bool,
  isFilterPanelOpen: PropTypes.bool,
  isConfigPanelOpen: PropTypes.bool,
  isProcessPanelOpen: PropTypes.bool,
  isCopyInterpretationOpen: PropTypes.bool,
  fieldFormatIdentifier: PropTypes.string,
  appConfig: PropTypes.object,
  dataModel: PropTypes.object,
  eventService: PropTypes.object,
  dataFilter: PropTypes.object,
  localize: PropTypes.object,
  tabStateService: PropTypes.object,
  columnConfig: PropTypes.func,
  field: PropTypes.func,
  processPanelService: PropTypes.object,
  chartService: PropTypes.object,
};

SlidingNavTabs.defaultProps = {
  disableAllTabs: false,
  tabs: [],
  selectedTabIndex: 0,
  tabProps: {},
  isTabUpdated: false,
  isFilterPanelOpen: false,
  isConfigPanelOpen: false,
  isProcessPanelOpen: false,
  isCopyInterpretationOpen: false,
};

export default SlidingNavTabs;
